/**
 * TariffaRow — F-009.4 + F-010. Riga editabile del listino: etichetta tipologia,
 * prezzo agenti, prezzo privati, pulsante Salva. Salva disabilitato se nessun
 * prezzo è cambiato o se un valore è invalido; al salvataggio invia solo i
 * prezzi effettivamente modificati. Presentazionale: `onSave`/`isPending`
 * arrivano da fuori (mutation).
 */
import { useState } from 'react';
import type { PraticaTipologiaValue } from '@app/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface TariffaRowProps {
  tipologia: PraticaTipologiaValue;
  label: string;
  /** Prezzo agenti corrente in cents (stringa) o null se non impostato. */
  prezzoCents: string | null;
  /** Prezzo privati corrente in cents (stringa) o null se non impostato. */
  prezzoPrivatiCents: string | null;
  onSave: (prices: { prezzoEuro?: number; prezzoPrivatiEuro?: number }) => void;
  isPending?: boolean;
}

function centsToEuroInput(prezzoCents: string | null): string {
  if (prezzoCents === null || prezzoCents === '') return '';
  const n = Number(prezzoCents);
  return Number.isNaN(n) ? '' : String(n / 100);
}

/** Parsing comune: accetta virgola decimale, ritorna numero o NaN. */
function parseEuro(value: string): number {
  return Number(value.replace(',', '.'));
}

export function TariffaRow({
  tipologia,
  label,
  prezzoCents,
  prezzoPrivatiCents,
  onSave,
  isPending,
}: TariffaRowProps) {
  const initialAgenti = centsToEuroInput(prezzoCents);
  const initialPrivati = centsToEuroInput(prezzoPrivatiCents);
  const [agenti, setAgenti] = useState(initialAgenti);
  const [privati, setPrivati] = useState(initialPrivati);

  const agentiNum = parseEuro(agenti);
  const privatiNum = parseEuro(privati);
  const agentiValid = agenti.trim() !== '' && Number.isFinite(agentiNum) && agentiNum >= 0;
  const privatiValid = privati.trim() !== '' && Number.isFinite(privatiNum) && privatiNum >= 0;

  const agentiChanged = agenti.trim() !== initialAgenti;
  const privatiChanged = privati.trim() !== initialPrivati;
  const anyChanged = agentiChanged || privatiChanged;

  // Salvabile se: c'è almeno una modifica e tutti i campi modificati sono validi.
  const canSave =
    anyChanged &&
    (!agentiChanged || agentiValid) &&
    (!privatiChanged || privatiValid) &&
    !isPending;

  function handleSave() {
    if (!canSave) return;
    onSave({
      ...(agentiChanged ? { prezzoEuro: agentiNum } : {}),
      ...(privatiChanged ? { prezzoPrivatiEuro: privatiNum } : {}),
    });
  }

  return (
    <tr className="border-b last:border-0" data-tipologia={tipologia}>
      <td className="py-3 pr-4 font-medium">{label}</td>
      <td className="py-3 pr-4">
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">€</span>
          <Input
            type="number"
            inputMode="decimal"
            min={0}
            step="1"
            value={agenti}
            onChange={(e) => setAgenti(e.target.value)}
            className="w-28"
            aria-label={`Prezzo agenti ${label} in euro`}
          />
        </div>
      </td>
      <td className="py-3 pr-4">
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">€</span>
          <Input
            type="number"
            inputMode="decimal"
            min={0}
            step="1"
            value={privati}
            onChange={(e) => setPrivati(e.target.value)}
            className="w-28"
            aria-label={`Prezzo privati ${label} in euro`}
          />
        </div>
      </td>
      <td className="py-3 text-right">
        <Button size="sm" onClick={handleSave} disabled={!canSave}>
          Salva
        </Button>
      </td>
    </tr>
  );
}
